import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";

import { buildOmittedAssetUrl } from "../../../services/priceHistoryOmitted";
import BreakdownDialogTitle from "../../../routes/pricing-reports/BreakdownDialogTitle";
import ChartBlock, { chartHasSeriesData } from "../../../routes/pricing-reports/ChartBlock";
import ProductImageViewer from "../../../routes/pricing-reports/ProductImageViewer";
import { withDirectusAssetTransformKey } from "../../../routes/pricing-reports/pricingReportImageUrls";
import { buildCountPieChart } from "../ProductFrequency/productFrequencyUtils";

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: { top: 8, right: 16, bottom: 8, left: 8 },
  },
  plugins: {
    legend: {
      display: true,
      position: "right",
      labels: {
        boxWidth: 12,
        padding: 12,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.parsed) || 0;
          const total = (context.dataset?.data || []).reduce(
            (sum, point) => sum + (Number(point) || 0),
            0
          );
          const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";
          return `${context.label}: ${value.toLocaleString()} images (${percent}%)`;
        },
      },
    },
  },
};

function formatUploadedOn(value) {
  if (!value) {
    return "—";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
}

function retailerTitle(retailer) {
  if (!retailer) {
    return "Retailer";
  }
  return retailer.provinceLabel ? `${retailer.label}, ${retailer.provinceLabel}` : retailer.label;
}

function uploaderRows(images) {
  const groups = new Map();
  for (const image of images) {
    const key = image.uploaderEmail || image.uploaderLabel || "unknown";
    const existing = groups.get(key) || {
      id: key,
      label: image.uploaderLabel || "Unknown",
      email: image.uploaderEmail || "",
      count: 0,
    };
    existing.count += 1;
    groups.set(key, existing);
  }
  return Array.from(groups.values()).sort((left, right) => {
    const countDelta = right.count - left.count;
    if (countDelta !== 0) {
      return countDelta;
    }
    return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
  });
}

function CountTable({ headers, rows }) {
  return (
    <Table size="small" sx={{ backgroundColor: "#fff", border: "1px solid #eaeaea" }}>
      <TableHead>
        <TableRow>
          {headers.map((header) => (
            <TableCell key={header} sx={{ fontWeight: 600 }}>
              {header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            {row.cells.map((cell, index) => (
              <TableCell key={`${row.id}-${index}`}>{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const GALLERY_PAGE_SIZE = 48;

function ImageGallery({ images, selectedFileId, onSelect }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(images.length / GALLERY_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * GALLERY_PAGE_SIZE;
  const visibleImages = images.slice(start, start + GALLERY_PAGE_SIZE);
  const end = start + visibleImages.length;

  useEffect(() => {
    setPage(0);
  }, [images]);

  return (
    <Stack spacing={1}>
    <Box
      role="list"
      aria-label="Uploaded images"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: 1,
      }}
    >
      {visibleImages.map((image) => {
        const selected = image.fileId === selectedFileId;
        const { thumbnailUrl } = imageUrls(image.fileId);
        const caption = `${image.uploaderLabel}, ${formatUploadedOn(image.uploadedOn)}`;
        return (
          <Box
            key={image.fileId}
            component="button"
            type="button"
            role="listitem"
            aria-pressed={selected}
            aria-label={caption}
            onClick={() => onSelect(image.fileId)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 0.5,
              p: 0.75,
              m: 0,
              textAlign: "left",
              cursor: "pointer",
              backgroundColor: "#fff",
              border: selected ? "2px solid #960B10" : "1px solid #E6E1D9",
            }}
          >
            <Box
              component="img"
              src={thumbnailUrl}
              alt=""
              loading="lazy"
              sx={{ width: "100%", height: 96, objectFit: "cover", backgroundColor: "#F1ECE2" }}
            />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              {image.caption || image.uploaderLabel}
            </Typography>
            <Typography variant="caption" sx={{ lineHeight: 1.2, color: "#3A3A3A" }}>
              {formatUploadedOn(image.uploadedOn)}
            </Typography>
          </Box>
        );
      })}
    </Box>
      {images.length > GALLERY_PAGE_SIZE ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2">
            {start + 1}–{end} of {images.length.toLocaleString()}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}

function imageUrls(fileId) {
  const url = buildOmittedAssetUrl(fileId);
  return {
    url,
    thumbnailUrl: withDirectusAssetTransformKey(url),
  };
}

function ImagesBreakdown({ retailer }) {
  const images = retailer?.images || [];
  const [selectedFileId, setSelectedFileId] = useState("");
  const uploaders = useMemo(() => uploaderRows(images), [images]);
  const selected = images.find((image) => image.fileId === selectedFileId) || images[0] || null;

  useEffect(() => {
    setSelectedFileId(images[0]?.fileId || "");
  }, [retailer?.id, images]);

  if (images.length === 0) {
    return <Typography variant="body2">No images for this retailer.</Typography>;
  }

  const selectedUrls = selected ? imageUrls(selected.fileId) : { url: "" };

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {images.length.toLocaleString()} {images.length === 1 ? "image" : "images"} uploaded at this
        retailer.
      </Typography>
      <Box>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Breakdown</Typography>
        <CountTable
          headers={["Uploaded by", "Email", "Images"]}
          rows={uploaders.map((row) => ({
            id: row.id,
            cells: [row.label, row.email || "—", row.count.toLocaleString()],
          }))}
        />
      </Box>
      {selectedUrls.url ? (
        <ProductImageViewer
          src={selectedUrls.url}
          alt={`${selected.uploaderLabel} photo at ${retailer.label}`}
          minHeight={280}
          maxHeight={420}
        />
      ) : null}
      {selected ? (
        <Typography variant="body2">
          {selected.uploaderLabel}
          {selected.uploaderEmail ? ` (${selected.uploaderEmail})` : ""} ·{" "}
          {formatUploadedOn(selected.uploadedOn)}
          {selected.cuts?.length ? ` · ${selected.cuts.join(", ")}` : ""}
        </Typography>
      ) : null}
      <ImageGallery images={images} selectedFileId={selected?.fileId || ""} onSelect={setSelectedFileId} />
    </Stack>
  );
}

function retailerVisitRows(images) {
  const groups = new Map();
  let unlinked = 0;

  for (const image of images) {
    const retailers = image.retailers || [];
    if (retailers.length === 0) {
      unlinked += 1;
      continue;
    }
    for (const retailer of retailers) {
      const key = `${retailer.provinceLabel || ""}|${retailer.label}`;
      const existing = groups.get(key) || {
        id: key,
        label: retailer.label,
        provinceLabel: retailer.provinceLabel || "",
        count: 0,
      };
      existing.count += 1;
      groups.set(key, existing);
    }
  }

  const rows = Array.from(groups.values()).sort((left, right) => {
    const countDelta = right.count - left.count;
    if (countDelta !== 0) {
      return countDelta;
    }
    return String(left.label).localeCompare(String(right.label), undefined, { sensitivity: "base" });
  });
  if (unlinked > 0) {
    rows.push({
      id: "unlinked",
      label: "Not linked to a price history row",
      provinceLabel: "",
      count: unlinked,
    });
  }
  return rows;
}

function UploadsBreakdown({ person }) {
  const images = person?.images || [];
  const [selectedFileId, setSelectedFileId] = useState("");
  const visits = useMemo(() => retailerVisitRows(images), [images]);
  const selected = images.find((image) => image.fileId === selectedFileId) || images[0] || null;

  useEffect(() => {
    setSelectedFileId(images[0]?.fileId || "");
  }, [person?.id, images]);

  if (images.length === 0) {
    return <Typography variant="body2">No images for this person.</Typography>;
  }

  const selectedUrls = selected ? imageUrls(selected.fileId) : { url: "" };
  const placeLabel = selected?.caption || "Not linked to a price history row";

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {`${images.length.toLocaleString()} ${images.length === 1 ? "image" : "images"} uploaded by ${person.label}${person.email ? ` (${person.email})` : ""}.`}
      </Typography>
      <Box>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Breakdown</Typography>
        <CountTable
          headers={["Retailer", "Province", "Images"]}
          rows={visits.map((row) => ({
            id: row.id,
            cells: [row.label, row.provinceLabel || "—", row.count.toLocaleString()],
          }))}
        />
      </Box>
      {selectedUrls.url ? (
        <ProductImageViewer
          src={selectedUrls.url}
          alt={`${person.label} photo`}
          minHeight={280}
          maxHeight={420}
        />
      ) : null}
      {selected ? (
        <Typography variant="body2">
          {formatUploadedOn(selected.uploadedOn)} · {placeLabel}
          {selected.cuts?.length ? ` · ${selected.cuts.join(", ")}` : ""}
        </Typography>
      ) : null}
      <ImageGallery images={images} selectedFileId={selected?.fileId || ""} onSelect={setSelectedFileId} />
    </Stack>
  );
}

const TOP_BEEF_CUTS = 10;

function CutsBreakdown({ retailer }) {
  const cutCounts = retailer?.cutCounts || [];
  const chart = useMemo(
    () =>
      buildCountPieChart(cutCounts.slice(0, TOP_BEEF_CUTS), {
        labelKey: "label",
        datasetLabel: "Images",
      }),
    [cutCounts]
  );
  const imageCount = Number(retailer?.count) || 0;
  const chartTitle = cutCounts.length > TOP_BEEF_CUTS ? "Top 10 beef cuts" : "Beef cuts";

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Beef cuts on the {imageCount.toLocaleString()} {imageCount === 1 ? "photo" : "photos"}{" "}
        uploaded at this retailer. A photo with more than one cut is counted in each cut.
      </Typography>
      <ChartBlock
        title={chartTitle}
        chartType="pie"
        chart={chart}
        chartOptions={pieOptions}
        hasData={chartHasSeriesData(chart)}
        emptyMessage="No beef cuts were found on these photos."
        framed
      />
      {cutCounts.length > 0 ? (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Breakdown</Typography>
          <CountTable
            headers={["Beef cut", "Images"]}
            rows={cutCounts.map((row) => ({
              id: row.id,
              cells: [row.label, Number(row.count || 0).toLocaleString()],
            }))}
          />
        </Box>
      ) : null}
    </Stack>
  );
}

export default function StreetTeamRetailerDialog({
  open,
  mode = "images",
  retailer,
  onClose,
  onExited,
}) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      className="insights-dialog"
      open={open}
      onClose={onClose}
      maxWidth={mode === "cuts" ? "md" : "lg"}
      fullWidth
      fullScreen={isMobileViewport}
      TransitionProps={{ onExited }}
    >
      <BreakdownDialogTitle
        title={mode === "uploads" ? retailer?.label || "Uploads" : retailerTitle(retailer)}
        onClose={onClose}
      />
      <DialogContent dividers>
        {mode === "cuts" ? (
          <CutsBreakdown retailer={retailer} />
        ) : mode === "uploads" ? (
          <UploadsBreakdown person={retailer} />
        ) : (
          <ImagesBreakdown retailer={retailer} />
        )}
      </DialogContent>
    </Dialog>
  );
}
